import * as React from "react";
import { HashTrigger } from "../../hash-trigger/hash-trigger.mjs";
import { PropsChecker } from "../../helpers/props-checker/props-checker.mjs";
import { useModal } from "../use-modal.mjs";
const ModalContext = React.createContext({
  isClosable: true,
  isOpen: false,
  open: (
    /* istanbul ignore next */
    () => null
  ),
  close: () => null,
  trigger: null,
  triggerRef: (
    /* istanbul ignore next */
    () => null
  )
});
const ModalProvider = (props) => {
  return /* @__PURE__ */ React.createElement(
    PropsChecker,
    {
      componentName: "ModalProvider",
      componentProps: props,
      defaultValueName: "defaultIsOpen",
      valueName: "isOpen",
      onChangeNames: "onToggle",
      onChangeAltNames: ["onOpen", "onClose"]
    },
    /* @__PURE__ */ React.createElement(ModalProviderInner, { ...props })
  );
};
const ModalProviderInner = (props) => {
  const { children, id, defaultIsOpen = false, onToggle, onOpen: setControlledOpen, onClose, isClosable = true, isOpen: controlledOpen } = props;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultIsOpen);
  const [trigger, setTrigger] = React.useState(null);
  const isOpen = React.useMemo(() => {
    return (onToggle || setControlledOpen && onClose) && typeof controlledOpen !== "undefined" ? controlledOpen : uncontrolledOpen;
  }, [controlledOpen, onClose, onToggle, setControlledOpen, uncontrolledOpen]);
  const openModal = React.useCallback(() => {
    if (typeof controlledOpen === "undefined") {
      setUncontrolledOpen(true);
    }
    if (onToggle) {
      onToggle(true);
    }
    if (setControlledOpen) {
      setControlledOpen();
    }
  }, [controlledOpen, onToggle, setControlledOpen]);
  const closeModal = React.useCallback(
    (event, childOnClick) => {
      if (typeof controlledOpen === "undefined") {
        setUncontrolledOpen(false);
      }
      if (onToggle) {
        onToggle(false);
      }
      if (onClose) {
        onClose();
      }
      if (childOnClick) {
        childOnClick(event);
      }
    },
    [controlledOpen, onClose, onToggle]
  );
  const handleOpenChange = React.useCallback(
    (nextOpen, event) => {
      if (nextOpen) {
        openModal();
      } else {
        closeModal(event);
      }
    },
    [openModal, closeModal]
  );
  const modalContext = useModal({
    open: isOpen,
    onOpenChange: handleOpenChange,
    isClosable
  });
  const renderContent = () => {
    return id ? /* @__PURE__ */ React.createElement(HashTrigger, { id, onMatch: openModal, scrollOnMatch: false, renderWrapper: false }, children) : /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  };
  const contextValue = React.useMemo(
    () => ({
      isOpen,
      close: closeModal,
      open: openModal,
      id,
      isClosable,
      trigger,
      triggerRef: (trigger2) => setTrigger(trigger2),
      state: modalContext
    }),
    [closeModal, id, isClosable, modalContext, openModal, trigger, isOpen]
  );
  return /* @__PURE__ */ React.createElement(ModalContext.Provider, { value: contextValue }, renderContent());
};
export {
  ModalContext,
  ModalProvider
};
